import type { useStore } from 'jotai'

import { STORAGE_KEYS } from '@/lib/configs/storage-config'
import { randomUUID } from '@/lib/utils'
import {
  activateAskChatThreadAtom,
  activeThreadIdAtomFamily,
  ASSISTANT_CARD_ID,
  ASSISTANT_SURFACE_ID,
  assistantPageMountedAtom,
  assistantPanelOpenAtom,
  assistantPendingPromptAtom,
  createChatThreadId,
  messagesAtomFamily,
  registerChatThreadAtom,
} from '@/state/chat'
import { type ResearchJob, researchJobsAtom } from '@/state/research'

type Store = ReturnType<typeof useStore>

// Tab-scoped (sessionStorage): "this browser session" survives a reload, not a new tab. Its
// start is stamped when the app loads (this module is imported by the global shortcuts), so
// research asked for before the sheet's first open still counts.
const SESSION_STARTED_KEY = STORAGE_KEYS.assistant.sessionStartedAt
const NEW_CHAT_AT_KEY = STORAGE_KEYS.assistant.newChatAt
if (typeof window !== 'undefined') readSessionTime(SESSION_STARTED_KEY, Date.now())

export type AssistantThreadRequest =
  /** ⌘K / `C`. On the assistant page: stay on its thread, or jump to an unseen research result.
   *  As the floating sheet: a new chat, unless research asked for in this browser session is
   *  still unseen (see pickSessionResearchJob). */
  | { kind: 'resume' }
  /** A blank thread — the sidebar's New Chat. */
  | { kind: 'new' }
  /** A specific thread (the recent list, a research notification). */
  | { kind: 'existing'; threadId: string }

export interface OpenAssistantOptions {
  thread?: AssistantThreadRequest
  /** Sent into the opened thread as if typed in its composer. */
  prompt?: string
}

/**
 * The one way to bring up the assistant. Every entry point (sidebar, ⌘K, `C`, notifications,
 * the recent list, the Order Form's prompt bar, a widget's ✨ Explain) goes through here, so they
 * all land in the same place: the /trade/assistant page when it's open, else the floating
 * assistant panel over the dashboard. The assistant is research-only; trades are drafted by the
 * Order Form's prompt bar. Returns the thread it opened.
 */
export function openAssistant(
  store: Store,
  { thread = { kind: 'resume' }, prompt }: OpenAssistantOptions = {}
): string {
  const surfaceId = ASSISTANT_SURFACE_ID
  const now = Date.now()

  switch (thread.kind) {
    case 'resume': {
      if (store.get(assistantPageMountedAtom)) {
        store.set(activateAskChatThreadAtom, { surfaceId })
        break
      }
      // Already open as the sheet: leave the conversation in progress alone.
      if (store.get(assistantPanelOpenAtom)) break
      const job = pickSessionResearchJob(
        Object.values(store.get(researchJobsAtom)),
        Math.max(readSessionTime(SESSION_STARTED_KEY, now), readSessionTime(NEW_CHAT_AT_KEY, 0))
      )
      // Viewing it marks it seen (Chat does on delivery), so the next open is a new chat again.
      if (job) store.set(activeThreadIdAtomFamily(surfaceId), job.threadId)
      // An untouched thread is already a new chat — don't stack empty ones.
      else if (store.get(messagesAtomFamily(store.get(activeThreadIdAtomFamily(surfaceId)))).length)
        openNewThread(store, now)
      break
    }
    case 'new': {
      writeSessionTime(NEW_CHAT_AT_KEY, now)
      openNewThread(store, now)
      break
    }
    case 'existing':
      store.set(activeThreadIdAtomFamily(surfaceId), thread.threadId)
      break
  }

  const threadId = store.get(activeThreadIdAtomFamily(surfaceId))
  if (prompt?.trim()) {
    store.set(assistantPendingPromptAtom, {
      requestId: randomUUID(),
      threadId,
      text: prompt.trim(),
    })
  }

  if (!store.get(assistantPageMountedAtom)) store.set(assistantPanelOpenAtom, true)
  focusAssistantComposer()
  return threadId
}

function openNewThread(store: Store, now: number) {
  const threadId = createChatThreadId()
  store.set(registerChatThreadAtom, {
    threadId,
    surfaceId: ASSISTANT_SURFACE_ID,
    cardId: ASSISTANT_CARD_ID,
    source: 'panel',
    title: '',
    createdAt: now,
    updatedAt: now,
  })
  store.set(activeThreadIdAtomFamily(ASSISTANT_SURFACE_ID), threadId)
}

/**
 * The research the sheet reopens on: the newest job asked for since `since` (this browser
 * session's start, or the user's last New Chat if later) that the user hasn't seen yet —
 * running, or delivered but unopened. Older sessions' unseen results stay in the recent list
 * and notifications; the sheet itself opens on a new chat.
 */
export function pickSessionResearchJob(
  jobs: ResearchJob[],
  since: number
): ResearchJob | undefined {
  return jobs
    .filter(
      (job) =>
        job.startedAt >= since &&
        !job.viewedAt &&
        job.phase !== 'CANCELLED' &&
        job.phase !== 'KILLED'
    )
    .sort((a, b) => b.startedAt - a.startedAt)[0]
}

function readSessionTime(key: string, fallback: number): number {
  try {
    const stored = Number(window.sessionStorage.getItem(key))
    if (stored > 0) return stored
    if (fallback > 0) window.sessionStorage.setItem(key, String(fallback))
  } catch {
    // Storage blocked: this page load is the session.
  }
  return fallback
}

function writeSessionTime(key: string, value: number) {
  try {
    window.sessionStorage.setItem(key, String(value))
  } catch {
    // Storage blocked: nothing to remember.
  }
}

/**
 * Wait for the composer, then focus it: the panel mounts a frame later, and the first time it
 * opens its (lazy) surface chunk has to load before the composer exists. Gives up after ~1s.
 * Skipped on touch devices.
 */
function focusAssistantComposer(framesLeft = 60) {
  // Touch devices: a programmatic focus raises the keyboard over the greeting before the user
  // asked for it (ChatGPT/Claude mobile wait for a tap on the field too).
  if (window.matchMedia('(pointer: coarse)').matches) return
  requestAnimationFrame(() => {
    const composer = document.querySelector<HTMLTextAreaElement>(
      '[data-assistant-surface] textarea'
    )
    if (composer) composer.focus({ preventScroll: true })
    else if (framesLeft > 0) focusAssistantComposer(framesLeft - 1)
  })
}
